const algorithm = "AES-GCM" // GCM mode is recommended for security
const keyLength = 256 // 256-bit key
const SHOPIFY_CLIENT_SECRET = "92b385bf5e82ee6f4a8af2074b4ff044"
export const ENCRYPTION_KEY = "92b385bf5e82ee6f4a8af2074b4ff044"

// Utility to encode/decode
// Convert Uint8Array to Base64 string
const encode = (data: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(data)))

// Convert Base64 string to Uint8Array
const decode = (data: string) =>
  new Uint8Array([...atob(data)].map((char) => char.charCodeAt(0)))

// Generate AES Key from Secret
async function generateKey(secret: string) {
  const encoder = new TextEncoder()
  const keyMaterial = encoder.encode(secret) // Use secret key
  return crypto.subtle.importKey(
    "raw",
    keyMaterial,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  )
}

// Derive AES Key
async function deriveKey(secretKey: string) {
  const keyMaterial = await generateKey(secretKey)
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new Uint8Array([1, 2, 3, 4]),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: algorithm, length: keyLength },
    false,
    ["encrypt", "decrypt"],
  )
}

// Encrypt JSON
export async function encryptJson(json: unknown, secretKey: string) {
  const key = await deriveKey(secretKey)
  const iv = crypto.getRandomValues(new Uint8Array(12)) // Random IV
  const encoder = new TextEncoder()
  const ciphertext = await crypto.subtle.encrypt(
    { name: algorithm, iv },
    key,
    encoder.encode(JSON.stringify(json)),
  )
  return {
    encrypted: `${encode(iv)}:${encode(new Uint8Array(ciphertext))}`,
    iv: encode(iv),
  }
}

// Decrypt JSON
export async function decryptJson(encryptedString: string, secretKey: string) {
  const [ivBase64, ciphertextBase64] = encryptedString.split(":")
  const key = await deriveKey(secretKey)
  const iv = decode(ivBase64)
  const ciphertext = decode(ciphertextBase64)
  const decoder = new TextDecoder()
  const plaintext = await crypto.subtle.decrypt(
    { name: algorithm, iv },
    key,
    ciphertext,
  )
  return JSON.parse(decoder.decode(plaintext))
}

// Generate HMAC
export async function generateHmac(
  data: string,
  secretKey: string,
  outputFormat: "base64" | "base64url" = "base64",
) {
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secretKey),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  )
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(data))

  // Convert to appropriate format
  if (outputFormat === "base64url") {
    return encode(new Uint8Array(signature))
  } else {
    // Standard base64 for AppSumo
    return btoa(String.fromCharCode(...new Uint8Array(signature)))
  }
}

// Verify HMAC
export async function verifyHmac(
  data: string,
  hmac: string,
  secretKey: string,
) {
  const computedHmac = await generateHmac(data, secretKey, "base64")
  return computedHmac === hmac
}

// Validate Shopify Webhook
export async function validateShopifyWebhook(
  body: string,
  hmac: string | null,
): Promise<boolean> {
  if (!hmac) return false

  // Convert the message and key to Uint8Arrays
  const encoder = new TextEncoder()
  const key = encoder.encode(SHOPIFY_CLIENT_SECRET)
  const message = encoder.encode(body)

  // Import the key
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  )

  // Sign the message
  const signature = await crypto.subtle.sign("HMAC", cryptoKey, message)

  // Convert the signature to base64
  const signatureBase64 = btoa(
    String.fromCharCode(...new Uint8Array(signature)),
  )

  return signatureBase64 === hmac
}
